import { AppState } from '@redux/store'
import { createSlice, isAnyOf, PayloadAction } from '@reduxjs/toolkit'
import { Form } from '@typesApp/Forms'
import { ViewName } from '@typesApp/PageFlow'
import { Insurer, InsurerErrorType, ProviderType } from '@typesApp/Insurer'
import { FormChangeState, FormErrorType } from '@typesApp/InsuranceForm'
import { claimRecordsApi } from '@redux/queries/claimRecords'
import { connectorSlice } from './connectorSlice'

type FormState = {
  insurer?: Insurer
  insurerError?: InsurerErrorType
  insurerProviderType?: ProviderType
  forms: Form[]
  index: number
  changeState?: FormChangeState
  errors: Record<string, FormErrorType>
  isInsurerLoading?: boolean
  formsReady?: boolean
}

const initialState: FormState = {
  forms: [],
  index: -1,
  errors: {},
}

const REDUCER_PATH = 'forms'

export const formsSlice = createSlice({
  name: REDUCER_PATH,
  initialState: initialState,
  reducers: {
    setForms(state, action: PayloadAction<Form[]>) {
      state.forms = action.payload
      if (state.index === -1 && action.payload.length) {
        state.index = 0
      }
    },
    initLastForm(state) {
      state.index = (state.forms?.length ?? 0) - 1
    },
    resetLastForm(state) {
      state.index = state.index !== -1 ? 0 : -1
      state.formsReady = true
    },
    setInsurerLoading(state, action: PayloadAction<boolean>) {
      state.isInsurerLoading = action.payload
    },
    selectInsurer(state, action: PayloadAction<{ insurer: Insurer; forms: Form[] }>) {
      // clear insurer error if selection is different
      if (state.insurer?.id !== action.payload?.insurer?.id) {
        state.insurerError = undefined
      }
      state.insurer = action.payload.insurer
      state.forms = action.payload.forms
    },
    setInsurerProviderType(state, action: PayloadAction<ProviderType>) {
      state.insurerProviderType = action.payload
    },
    resetInsurerProviderType(state) {
      state.insurerProviderType = undefined
    },
    nextForm(state) {
      return {
        ...state,
        index: state.index !== -1 && state.index < state?.forms?.length - 1 ? state.index + 1 : state.index,
      }
    },
    previousForm(state) {
      return {
        ...state,
        index: state.index !== -1 && state.index > 0 ? state.index - 1 : state.index,
      }
    },
    formAtIndex(state, action: PayloadAction<number>) {
      return {
        ...state,
        index: action.payload,
      }
    },
    setFormChangeState(state, action: PayloadAction<FormChangeState>) {
      return {
        ...state,
        changeState: action.payload,
      }
    },
    setFormError(state, action: PayloadAction<{ view: ViewName; error: FormErrorType }>) {
      if (state.errors) {
        state.errors[action.payload.view] = action.payload.error
      } else {
        state.errors = {
          [action.payload.view]: action.payload.error,
        }
      }
    },
    setInsurerError(state, action: PayloadAction<InsurerErrorType>) {
      if (state.insurer) {
        state.insurerError = action.payload
      }
    },
    resetFormError(state, action: PayloadAction<ViewName>) {
      if (state.errors) {
        const errors = { ...state.errors }
        delete errors[action.payload]
        state.errors = errors
      }
    },
    resetFormChangeState(state) {
      return {
        ...state,
        changeState: undefined,
      }
    },
    resetForms(state) {
      return {
        ...state,
        index: state.index !== -1 ? 0 : -1,
        changeState: undefined,
        errors: {},
        insurer: undefined,
        insurerError: undefined,
        formsReady: undefined,
      }
    },
    resetFormsInitialState() {
      return initialState
    },
  },
  extraReducers: builder => {
    /**
     * Reset selected insurer
     */
    builder.addMatcher(
      isAnyOf(
        connectorSlice.actions.connectorOrderComplete,
        claimRecordsApi.endpoints.createClaimRecord.matchFulfilled,
        claimRecordsApi.endpoints.deleteClaimRecord.matchFulfilled,
        claimRecordsApi.endpoints.nullifyClaim.matchFulfilled,
        claimRecordsApi.endpoints.submitClaim.matchFulfilled
      ),
      state => {
        state.insurer = undefined
        state.insurerError = undefined
        state.insurerProviderType = undefined
      }
    )

    /**
     * Reset to initial state on session expiry or logout
     */
    builder.addMatcher(
      isAnyOf(connectorSlice.actions.sessionExpired, connectorSlice.actions.connectorLogoutInProgress),
      () => {
        return initialState
      }
    )
  },
})

export const {
  setForms,
  selectInsurer,
  setInsurerProviderType,
  resetInsurerProviderType,
  nextForm,
  previousForm,
  formAtIndex,
  resetForms,
  initLastForm,
  resetLastForm,
  setFormError,
  resetFormError,
  setInsurerError,
  setFormChangeState,
  resetFormChangeState,
  setInsurerLoading,
  resetFormsInitialState,
} = formsSlice.actions

export const formsStateSelector = (state: AppState): FormState => state.forms
export const selectedInsurerSelector = (state: AppState): Insurer | undefined => state.forms.insurer
export const selectedInsurerErrorSelector = (state: AppState): InsurerErrorType | undefined => state.forms.insurerError
export const selectedInsurerProviderTypeSelector = (state: AppState): ProviderType | undefined =>
  state.forms.insurerProviderType
export const formChangeStateSelector = (state: AppState): FormChangeState | undefined => state.forms.changeState
export const formErrorsSelector = (state: AppState): Record<ViewName, FormErrorType> => state.forms.errors
export const insurerLoadingSelector = (state: AppState): boolean => state.forms.isInsurerLoading === true
export const formsReadySelector = (state: AppState): boolean => state.forms.formsReady === true
